import type {
  CharacterState,
  DerivedStats,
  Rules,
  SpellPointBudget,
} from "../data/types";

export function calcDerivedStats(
  state: CharacterState,
  rules: Rules
): DerivedStats {
  const { maxHp, maxWealthFromSkills: maxWealth } = rules.limits;
  let hp = rules.limits.baseHp;
  let mp = rules.limits.baseMp;
  let wealthLevel = rules.limits.baseWealth;
  let skillPoints = rules.limits.initialSkillPoints + state.bonusSkillPoints;
  let darkOfferingsLimit = rules.limits.darkOfferingsLimit;

  // Process core skills
  for (const sel of state.coreSkillSelections) {
    const skill = rules.coreSkills.find((s) => s.id === sel.skillId);
    if (!skill) continue;
    for (let i = 0; i < sel.count; i++) {
      for (const effect of skill.effects) {
        if (effect.type === "hp") hp = Math.min(hp + effect.value, maxHp);
        if (effect.type === "mp") mp += effect.value;
        if (effect.type === "wealthLevel")
          wealthLevel = Math.min(wealthLevel + effect.value, maxWealth);
      }
    }
  }

  const allPacts = [...state.pacts, ...(state.bonusPacts || [])];

  // Build offering swap map from Gift of Choice pacts AND standalone gifts
  const swapMap = new Map<number, number>();
  for (const pact of allPacts) {
    if (
      pact.giftId === "gift-of-choice" &&
      pact.choiceSwapFromId &&
      pact.choiceSwapToId
    ) {
      swapMap.set(pact.choiceSwapFromId, pact.choiceSwapToId);
    }
  }
  for (const sg of state.standaloneGifts || []) {
    if (
      sg.giftId === "gift-of-choice" &&
      sg.choiceSwapFromId &&
      sg.choiceSwapToId
    ) {
      swapMap.set(sg.choiceSwapFromId, sg.choiceSwapToId);
    }
  }
  // Count-based won-back tracking: same offering can be won back multiple times
  const buildWonBackCounts = () => {
    const m = new Map<number, number>();
    for (const id of state.wonBackOfferings || [])
      m.set(id, (m.get(id) || 0) + 1);
    return m;
  };
  const makeConsumeWonBack = (counts: Map<number, number>) => (id: number) => {
    const c = counts.get(id) || 0;
    if (c > 0) {
      counts.set(id, c - 1);
      return true;
    }
    return false;
  };

  // Process dark offerings effects with swaps, skipping won-back instances
  const wonBackForEffects = buildWonBackCounts();
  const consumeWonBackEffect = makeConsumeWonBack(wonBackForEffects);
  for (const pact of allPacts) {
    const effectiveId = swapMap.get(pact.darkOfferingId) ?? pact.darkOfferingId;
    if (consumeWonBackEffect(effectiveId)) continue;
    const offering = rules.darkOfferings.find((o) => o.id === effectiveId);
    if (offering) {
      for (const effect of offering.effects) {
        if (effect.type === "wealthLevel")
          wealthLevel = Math.max(0, wealthLevel + effect.value);
      }
    }
  }
  // Standalone (forced) offerings
  for (const offeringId of state.standaloneOfferings || []) {
    if (consumeWonBackEffect(offeringId)) continue;
    const offering = rules.darkOfferings.find((o) => o.id === offeringId);
    if (offering) {
      for (const effect of offering.effects) {
        if (effect.type === "wealthLevel")
          wealthLevel = Math.max(0, wealthLevel + effect.value);
      }
    }
  }

  // Process otherworldly gifts
  for (const pact of allPacts) {
    const gift = rules.otherworldlyGifts.find((g) => g.id === pact.giftId);
    if (!gift) continue;
    for (const effect of gift.effects) {
      if (effect.type === "hp") hp = Math.min(hp + effect.value, maxHp);
      if (effect.type === "mp") mp += effect.value;
      if (effect.type === "wealthLevel")
        wealthLevel = Math.min(wealthLevel + effect.value, maxWealth);
      // 'skillPoints' from Gift of Earthly Experience is consumed inline (see giftSkillId below)
    }
    if (gift.id === "gift-of-life-everlasting") {
      darkOfferingsLimit = 7;
    }
    // Apply the core skill chosen via Gift of Earthly Experience
    if (gift.id === "gift-of-earthly-experience" && pact.giftSkillId) {
      const chosenSkill = rules.coreSkills.find(
        (s) => s.id === pact.giftSkillId
      );
      if (chosenSkill) {
        for (const effect of chosenSkill.effects) {
          if (effect.type === "hp") hp = Math.min(hp + effect.value, maxHp);
          if (effect.type === "mp") mp += effect.value;
          if (effect.type === "wealthLevel")
            wealthLevel = Math.min(wealthLevel + effect.value, maxWealth);
        }
      }
    }
  }

  const budget: SpellPointBudget = {
    any: 0,
    destruction: 0,
    warMagic: 0,
    protection: 0,
    sorcery: 0,
  };

  // From core skills (Arcane Prodigy)
  for (const sel of state.coreSkillSelections) {
    const skill = rules.coreSkills.find((s) => s.id === sel.skillId);
    if (!skill?.spellPoints) continue;
    const sp = skill.spellPoints;
    const total = sp.points * sel.count;
    if (sp.listId === "any") budget.any += total;
    else
      budget[sp.listId as keyof SpellPointBudget] =
        (budget[sp.listId as keyof SpellPointBudget] || 0) + total;
  }

  // From gifts (and any giftSkillId that grants spell points, e.g. Arcane Prodigy)
  for (const pact of allPacts) {
    const gift = rules.otherworldlyGifts.find((g) => g.id === pact.giftId);
    if (gift?.spellPoints) {
      const sp = gift.spellPoints;
      if (sp.listId === "any") budget.any += sp.points;
      else
        budget[sp.listId as keyof SpellPointBudget] =
          (budget[sp.listId as keyof SpellPointBudget] || 0) + sp.points;
    }
    if (gift?.id === "gift-of-earthly-experience" && pact.giftSkillId) {
      const chosenSkill = rules.coreSkills.find(
        (s) => s.id === pact.giftSkillId
      );
      if (chosenSkill?.spellPoints) {
        const sp = chosenSkill.spellPoints;
        if (sp.listId === "any") budget.any += sp.points;
        else
          budget[sp.listId as keyof SpellPointBudget] =
            (budget[sp.listId as keyof SpellPointBudget] || 0) + sp.points;
      }
    }
  }

  // From standalone gifts (mission rewards etc.)
  for (const sg of state.standaloneGifts || []) {
    const gift = rules.otherworldlyGifts.find((g) => g.id === sg.giftId);
    if (!gift) continue;
    for (const effect of gift.effects) {
      if (effect.type === "hp") hp = Math.min(hp + effect.value, maxHp);
      if (effect.type === "mp") mp += effect.value;
      if (effect.type === "wealthLevel")
        wealthLevel = Math.min(wealthLevel + effect.value, maxWealth);
    }
    if (gift.id === "gift-of-life-everlasting") darkOfferingsLimit = 7;
    if (gift.id === "gift-of-earthly-experience" && sg.giftSkillId) {
      const chosenSkill = rules.coreSkills.find((s) => s.id === sg.giftSkillId);
      if (chosenSkill) {
        for (const effect of chosenSkill.effects) {
          if (effect.type === "hp") hp = Math.min(hp + effect.value, maxHp);
          if (effect.type === "mp") mp += effect.value;
          if (effect.type === "wealthLevel")
            wealthLevel = Math.min(wealthLevel + effect.value, maxWealth);
        }
        if (chosenSkill.spellPoints) {
          const sp = chosenSkill.spellPoints;
          if (sp.listId === "any") budget.any += sp.points;
          else
            budget[sp.listId as keyof SpellPointBudget] =
              (budget[sp.listId as keyof SpellPointBudget] || 0) + sp.points;
        }
      }
    }
    if (gift.spellPoints) {
      const sp = gift.spellPoints;
      if (sp.listId === "any") budget.any += sp.points;
      else
        budget[sp.listId as keyof SpellPointBudget] =
          (budget[sp.listId as keyof SpellPointBudget] || 0) + sp.points;
    }
  }

  const usedSpellPoints: Record<string, number> = {};
  const knownSpells: string[] = [];
  for (const alloc of state.spellAllocations) {
    if (alloc.points > 0) {
      usedSpellPoints[alloc.spellId] = alloc.points;
      knownSpells.push(alloc.spellId);
    }
  }

  const usedSkillPoints = state.coreSkillSelections.reduce(
    (sum, s) => sum + s.count,
    0
  );

  // Active offerings count using fresh won-back counts (count-based)
  const wonBackForCount = buildWonBackCounts();
  const consumeWonBackCount = makeConsumeWonBack(wonBackForCount);
  const activeFromPacts = allPacts.filter((p) => {
    const effectiveId = swapMap.get(p.darkOfferingId) ?? p.darkOfferingId;
    return !consumeWonBackCount(effectiveId);
  }).length;
  const activeStandalone = (state.standaloneOfferings || []).filter(
    (id) => !consumeWonBackCount(id)
  ).length;
  const activeResurrection = (state.resurrectionPacts || []).filter(
    (rp) => !consumeWonBackCount(rp.darkOfferingId)
  ).length;
  const totalDarkOfferings =
    activeFromPacts + activeStandalone + activeResurrection;

  // All patron IDs: from pacts + non-exchange standalone gifts + resurrection pacts
  const patronIds = [
    ...new Set([
      ...allPacts.map((p) => p.patronId).filter(Boolean),
      ...(state.standaloneGifts || [])
        .filter((sg) => sg.source !== "exchange")
        .map((sg) => sg.patronId)
        .filter((id): id is string => !!id),
      ...(state.resurrectionPacts || []).map((rp) => rp.patronId),
    ]),
  ];

  // Ultimate Warlock: once ascended, status is kept regardless of later Patron
  // dealings. Only rules with Ultimate stats change the numbers.
  const ultimate = state.ultimateWarlock ? rules.limits.ultimateWarlock : null;
  if (ultimate) {
    hp = ultimate.hp;
    mp = ultimate.mp;
    wealthLevel = ultimate.wealthLevel;
  }
  const hpCap = ultimate ? ultimate.hp : maxHp;
  const mpCap = ultimate ? ultimate.mp : rules.limits.maxMp;

  // Apply blood sacrifice HP penalty (-1 max HP per sacrifice)
  hp -= state.bloodSacrifices || 0;

  return {
    hp: Math.max(0, hp),
    maxHp: Math.min(hp, hpCap),
    mp: Math.max(0, mpCap === null ? mp : Math.min(mp, mpCap)),
    wealthLevel: Math.max(0, wealthLevel),
    totalDarkOfferings,
    darkOfferingsLimit,
    totalSkillPoints: skillPoints,
    usedSkillPoints,
    totalSpellPointBudget: budget,
    usedSpellPoints,
    knownSpells,
    patronIds,
    isUltimateWarlock: !!state.ultimateWarlock,
    maxSpellLevel: state.ultimateWarlock ? null : rules.limits.maxSpellLevel,
  };
}
